<template>
  <div class="change-password-container">
    <div class="form-container">
      <form class="change-password-form" method="post" @submit.prevent="handleSubmitNewPassword">
        <div class="password-label-section">
          <label for="current password">Current password</label>
          <span
            v-if="errorData.incorrectPassword"
            class="text-danger"
            data-test="incorrect-password"
            >{{ errorData.incorrectPassword }}</span
          >
          <span
            v-if="v$.currentPassword.required.$invalid && submitStatus === 'Error'"
            class="text-danger"
            data-test="current-password-required"
            >You must enter your current password</span
          >
          <div class="input-container">
            <input
              id="current password"
              v-model.trim="currentPassword"
              class="password-input"
              name="current password"
              type="password"
              @blur="v$.currentPassword.$touch()"
              data-test="current-password"
            />
            <i class="placeholder-icon"
              ><img alt="password lock icon" src="../../assets/registration/lock-icon.svg"
            /></i>
          </div>
        </div>
        <div class="password-label-section">
          <div class="new-password-label">
            <label for="new-password">New password</label>
            <img
              id="password-icon"
              alt="password rules information"
              class="info-icon"
              src="../../assets/registration/info-icon.svg"
              tabindex="0"
            />
            <tooltip
              data-html="true"
              target="#password-icon"
              text="
            <ul>
              <li>Must be a minimum of 8 characters in length</li>
              <li>Must include at least one number, one uppercase and one lowercase letter</li>
              <li>No spaces at the beginning or end</li>
            </ul>
            "
            />
          </div>
          <div
            v-if="v$.newPassword.required.$invalid && submitStatus === 'Error'"
            class="text-danger"
            data-test="new-password-required"
          >
            You must enter a new password
          </div>
          <div
            v-if="
              !v$.newPassword.required.$invalid &&
              v$.newPassword.minLength.$invalid &&
              submitStatus === 'Error'
            "
            class="text-danger"
            data-test="new-password-min"
          >
            Minimum 8 characters long.
          </div>
          <div
            v-if="
              !v$.newPassword.required.$invalid &&
              v$.newPassword.isPasswordValid.$invalid &&
              submitStatus === 'Error'
            "
            class="text-danger"
            data-test="invalid-password"
          >
            Must include a number, an upper and a lower case letter.
          </div>
          <div class="input-container">
            <input
              id="new-password"
              v-model.trim="newPassword"
              class="password-input"
              name="new password"
              type="password"
              @blur="v$.newPassword.$touch()"
              data-test="new-password"
              aria-label="new password"
            />
            <i class="placeholder-icon"
              ><img alt="password lock icon" src="../../assets/registration/lock-icon.svg"
            /></i>
          </div>
        </div>
        <div class="password-label-section">
          <label for="confirm password">Confirm new password</label>
          <span
            v-if="v$.confirmPassword.required.$invalid && submitStatus === 'Error'"
            class="text-danger"
            data-test="confirm-password-required"
          >
            You must confirm your new password
          </span>
          <span
            v-if="
              !v$.confirmPassword.required.$invalid &&
              v$.confirmPassword.sameAsPassword.$invalid &&
              submitStatus === 'Error'
            "
            class="text-danger"
            data-test="not-same-as"
            >Those passwords didn't match. Try again.</span
          >
          <div class="input-container">
            <input
              id="confirm password"
              v-model="confirmPassword"
              class="password-input"
              name="confirm password"
              type="password"
              @blur="v$.confirmPassword.$touch()"
              data-test="confirmed-password"
            />
            <i class="placeholder-icon"
              ><img alt="password lock icon" src="../../assets/registration/lock-icon.svg"
            /></i>
          </div>
        </div>
        <span v-if="errorData.serverErrorOccured" class="text-danger"
          >Something went wrong, please try again later.</span
        >
        <span v-if="errorData.otherErrorData" style="display: block" class="text-danger">{{
          errorData.otherErrorData
        }}</span>
        <div class="password-controls">
          <button
            class="btn btn-default"
            @click="toggleChangePassword"
            data-test="cancel-password"
            type="button"
          >
            Cancel
          </button>
          <button
            :class="canSubmit ? 'btn-primary' : 'btn-disabled'"
            class="btn"
            href="javascript:void(0)"
            type="submit"
            data-test="submit-new-password"
          >
            Change password
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import axios, { AxiosError, AxiosResponse } from "axios";
import { debug as d } from "debug";
import { useVuelidate } from "@vuelidate/core";
import { minLength, required, sameAs } from "@vuelidate/validators";
import { Tooltip } from "uiv";
import { ChangePasswordApiModel } from "../../api-models";

const debug = d("cs.ChangePassword");

interface ErrorData {
  incorrectPassword: string | null;
  otherErrorData: string | null;
  serverErrorOccured: boolean;
}

const emit = defineEmits(["toggle-change-password", "toggle-success-message"]);

function isPasswordValid(value: string): boolean {
  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/;
  const passwordArray = value.match(passwordRegex);
  if (passwordArray) {
    return passwordArray?.length > 0;
  }
  return false;
}

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

const submitStatus = ref("");
const attemptedToSubmit = ref(false);
const badRequest = ref(false);

const errorData = ref({
  incorrectPassword: null,
  otherErrorData: "",
  serverErrorOccured: false,
} as ErrorData);

const rules = {
  currentPassword: {
    required,
    minLength: minLength(6),
  },
  newPassword: {
    required,
    minLength: minLength(8),
    isPasswordValid,
  },
  confirmPassword: {
    required,
    sameAsPassword: sameAs(newPassword),
  },
};

const state = reactive({
  currentPassword,
  newPassword,
  confirmPassword,
});

const v$ = useVuelidate(rules, state, { $autoDirty: true });

function toggleChangePassword(): void {
  emit("toggle-change-password");
}

const canSubmit = computed(() => {
  return (
    !v$.value.currentPassword.required.$invalid &&
    !v$.value.newPassword.required.$invalid &&
    !v$.value.confirmPassword.required.$invalid
  );
});

function handleSubmit(): void {
  if (canSubmit.value) {
    const newPasswordModel: ChangePasswordApiModel = {
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
      confirmPassword: confirmPassword.value,
    };
    axios
      .post("/api/myaccount/changepassword", newPasswordModel)
      .then((resp: AxiosResponse) => {
        if (resp.status === 200 && resp.data) {
          if (resp.data === "Incorrect password.") {
            errorData.value.incorrectPassword = "Your current password is incorrect";
            return;
          }
          errorData.value = resp.data;
          return;
        }
        switch (resp.status) {
          case 200:
            emit("toggle-success-message", {
              isDetailsUpdated: true,
              detailChanged: "password",
            });
            toggleChangePassword();
            break;
          case 400:
            badRequest.value = true;
            break;
          case 500:
            errorData.value.serverErrorOccured = true;
            break;
          default:
            break;
        }
      })
      .catch((e: AxiosError) => {
        debug(e);
        if ((e.response?.request.responseURL as string).endsWith("500")) {
          errorData.value.serverErrorOccured = true;
        }
      });
  }
}

async function handleSubmitNewPassword(): Promise<void> {
  const isFormValid = await v$.value.$validate();
  attemptedToSubmit.value = true;
  if (!isFormValid) {
    submitStatus.value = "Error";
  } else {
    submitStatus.value = "OK";
    handleSubmit();
  }
}
</script>
<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/bootstrap.less";
@import (reference) "../../styles/bootstrap/variables.less";

.change-password-container {
  padding-inline: 1rem;
  width: 100%;
  min-height: 390px;
}

.form-container {
  width: 80%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (max-width: @screen-sm) {
    width: 100%;
  }
}

.change-password-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  min-height: 250px;
  margin: 5rem auto;
}

.password-label-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  color: @form-text-color;
  // position: relative;
  margin-bottom: 2rem;

  label {
    font-weight: normal;
  }
}

.password-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(@form-text-color, 0.5);
  background-color: @registration-background-color;
  height: 35px;
  max-width: 100%;
  padding-left: 30px;

  &:focus,
  &:active {
    outline: none;
    border-bottom: 1px solid @form-text-color;
  }

  &::placeholder {
    color: @practical-color;
  }
}

.input-container {
  position: relative;
}

.placeholder-icon {
  position: absolute;
  bottom: 10.5px;
  left: 10px;

  img {
    height: 14px;
  }
}

.btn {
  margin-top: 2rem;
}

.info-icon {
  margin-left: 0.5rem;
  display: inline;

  &:hover {
    cursor: pointer;
  }
}

.password-controls {
  text-align: center;

  .btn {
    margin: 1rem 2rem;
  }
}
</style>
